// Project: GalaxyComplete
// Created: 9/24/20 by sammy
// File: ScreenCommon

import * as React from "react";
import { observer } from "mobx-react-lite";
import { Alert, Box, Container, ContainerProps, Typography, useTheme } from "@mui/material";
import { useIsDesktop } from "./MainLayout";
import { useMountEffect } from "../../common/hooks/hookslib";
import { ErrorBoundary } from "@sentry/react";
import Grid from "@mui/material/Grid2";
import { useTranslation } from "react-i18next";

// ======================
// ScreenFrame
// ======================

const useScreenFrameStyles = () => {
    const t = useTheme();

    return {
        paddingTop: t.spacing(1),
        paddingBottom: t.spacing(10),
        minWidth: { xs: 0, md: 600 },
        maxWidth: 1920,
        marginLeft: 0,
    };
};

interface ScreenContainerProps {
    disableScrollToTop?: boolean;
}

export const ScreenContainer: React.FC<ScreenContainerProps & Partial<ContainerProps>> = observer((p) => {
    const styles = useScreenFrameStyles();
    const { t } = useTranslation();

    useMountEffect(() => {
        if (!p.disableScrollToTop) {
            window.scrollTo(0, 0);
        }
    });

    const sx = {
        ...styles,
        ...(p.sx || {}),
    };

    return (
        <Container sx={sx} maxWidth={false} {...p}>
            <ErrorBoundary fallback={<Alert severity={"error"}>{t("common:errors.generic")}</Alert>}>{p.children}</ErrorBoundary>
        </Container>
    );
});

// ======================
// ScreenTitleBar
// ======================

const useScreenTitleBarStyles = () => {
    const t = useTheme();
    return {
        root: {
            paddingBottom: t.spacing(2),
        },
    };
};

interface ScreenTitleBarProps {
    title: React.ReactNode;
    actions?: React.ReactNode;
}

export const ScreenTitleBar: React.FC<ScreenTitleBarProps> = observer((p) => {
    const styles = useScreenTitleBarStyles();
    const isDesktop = useIsDesktop();
    if (isDesktop) {
        return (
            <Grid container justifyContent={"space-between"} alignItems={"center"} sx={styles.root}>
                <Typography variant={"h4"}>{p.title}</Typography>
                {p.actions && <div>{p.actions}</div>}
            </Grid>
        );
    } else {
        return (
            <Box pb={2}>
                <Typography variant={"h3"}>{p.title}</Typography>
                {p.actions && (
                    <Box display={"flex"} justifyContent={"center"} pt={2}>
                        {p.actions}
                    </Box>
                )}
            </Box>
        );
    }
});
